import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TablePagination,
  Paper,
  Box,
  Button,
  TextField,
  InputAdornment,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  CircularProgress
} from "@mui/material";
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import Navbar from "../../../../globals/components/Navbar";
import { apiClient } from "../../../../api/httpClient";
import TablePaginationActions from "../../../../globals/components/TablePaginationActions";

function LawSchoolList() {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const [newSchoolName, setNewSchoolName] = useState("");
  const [lawSchools, setLawSchools] = useState([]);
  const [loading, setLoading] = useState(true); // Loading state

  // Fetch Law Schools from Backend
  useEffect(() => {
    fetchLawSchools();
  }, []);

  const fetchLawSchools = async () => {
    setLoading(true); // Set loading to true before fetching data
    try {
      const data = await apiClient("/lawschools/", "GET");
      setLawSchools(data.data);
    } catch (error) {
      console.error("Error fetching law schools:", error);
    } finally {
      setLoading(false); // Set loading to false after fetching data
    }
  };

  const filteredLawSchools = lawSchools.filter((school) =>
    school.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenDialog = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setNewSchoolName("");
  };

  const handleAddLawSchool = async () => {
    try {
      const newLawSchool = {
        name: newSchoolName,
        is_active: true,
      };

      const addedSchool = await apiClient("/lawschools/", "POST", newLawSchool);
      setLawSchools((prevSchools) => [...prevSchools, addedSchool]);
      handleCloseDialog();
    } catch (error) {
      console.error("Error adding law school:", error);
    }
  };

  const currentRows = filteredLawSchools.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <>
      <Navbar />

      <Box sx={{ maxWidth: "100%", padding: "20px" }}>
        {/* Heading */}
        <Typography variant="h4" sx={{ fontWeight: "bold", mb: 2 }}>
          Law Schools
        </Typography>

        {/* Search bar and New Law School Button */}
        <Box sx={{ display: "flex", justifyContent: "space-between", marginBottom: "20px" }}>
          <TextField
            label="Search by Law School Name"
            variant="outlined"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{
              width: "300px",
              borderRadius: "8px",
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
              },
            }}
            slotProps={{ input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}}
          />
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleOpenDialog}
          >
            New Law School
          </Button>
        </Box>

        {/* Loading Spinner */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Law School Table */}
            <TableContainer component={Paper} sx={{ width: "100%", overflowX: "auto", mb: 2 }}>
              <Table>
                <TableHead>
                  <TableRow style={{ backgroundColor: "#f0f0f0" }}>
                    <TableCell>Law School Name</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {currentRows.map((school, index) => (
                    <TableRow key={index}>
                      <TableCell>{school.name}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
          ActionsComponent={TablePaginationActions}
              component="div"
              count={filteredLawSchools.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25]}
            />
          </>
        )}
      </Box>

      {/* Dialog for Adding New Law School */}
      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle>Add New Law School</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Law School Name"
            type="text"
            fullWidth
            variant="outlined"
            value={newSchoolName}
            onChange={(e) => setNewSchoolName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleAddLawSchool} color="primary">
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default LawSchoolList;
